import { useCallback, useEffect, useMemo, useState } from 'react';
import { UMButton, UMFormStyles, UMIcon } from '../../urbanmap/_components';
import { PlanLogo } from '../proposition-c/ui';
import { countModifiedRatios, djuCoefficient, inPerimeter, stationOf } from './domain';
import { DjuForm } from './DjuForm';
import { useCreateProject } from './hooks';
import { ImpactPanel } from './ImpactPanel';
import { RatioTable } from './RatioTable';
import { StepPerimeter, type PerimeterDraft } from './StepPerimeter';
import { StepRecap, type CopyStatus } from './StepRecap';
import { PC, UMR } from './tokens';
import type { DjuSettings, Perimeter, RatioTable as Ratios, Referential } from './types';

interface Props {
  referential: Referential;
  onCancel: () => void;
  onOpen: (projectId: string) => void;
}

const STEPS = ['Nom et périmètre', 'Ratios de consommation', 'DJU'];

function Stepper({ current }: { current: number }) {
  return (
    <ol style={{ display: 'flex', gap: 8, listStyle: 'none', margin: 0, padding: '12px 24px', borderBottom: `1px solid ${UMR.border}`, background: UMR.grey100 }}>
      {STEPS.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li key={s} aria-current={active ? 'step' : undefined} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: active || done ? UMR.textStrong : UMR.textSecondary, fontWeight: active ? 700 : 400 }}>
            <span style={{ width: 24, height: 24, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, background: done || active ? PC.accent : UMR.white, color: done || active ? UMR.white : UMR.textSecondary, border: `1px solid ${done || active ? PC.accent : UMR.border}` }}>
              {done ? <UMIcon name="check" size={16} color={UMR.white} /> : i + 1}
            </span>
            {s}
            {i < STEPS.length - 1 && <span style={{ width: 40, height: 1, background: UMR.border, margin: '0 4px' }} />}
          </li>
        );
      })}
      {current >= STEPS.length && <li style={{ marginLeft: 'auto', fontSize: 14, fontWeight: 700, color: PC.accentStrong }}>Récapitulatif</li>}
    </ol>
  );
}

export function CreateWizard({ referential, onCancel, onOpen }: Props) {
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<PerimeterDraft>({ name: '', mode: 'communes', communes: [], points: [], closed: false, drawing: false });
  const [ratios, setRatios] = useState<Ratios>(referential.defaultRatios);
  const [dju, setDju] = useState<DjuSettings>(referential.defaultDju);
  const [ratiosValid, setRatiosValid] = useState(true);
  const [djuValid, setDjuValid] = useState(true);
  const [status, setStatus] = useState<CopyStatus>('idle');
  const [progress, setProgress] = useState(0);
  const create = useCreateProject();

  const patch = useCallback((p: Partial<PerimeterDraft>) => setDraft((d) => ({ ...d, ...p })), []);
  const perimeter = useMemo<Perimeter>(
    () => (draft.mode === 'communes' ? { kind: 'communes', communes: draft.communes } : { kind: 'drawn', polygon: draft.closed ? draft.points : [] }),
    [draft.mode, draft.communes, draft.points, draft.closed],
  );
  const included = useMemo(() => referential.buildings.filter((b) => inPerimeter(b, perimeter)), [referential.buildings, perimeter]);
  const includedIds = useMemo(() => new Set(included.map((b) => b.id)), [included]);
  const perimeterLabel = perimeter.kind === 'drawn' ? 'Contour dessiné' : draft.communes.map((c) => referential.communes.find((x) => x.insee === c)?.name ?? c).join(', ');

  useEffect(() => {
    if (status !== 'copying') return undefined;
    const t = window.setInterval(() => setProgress((p) => Math.min(create.isSuccess ? 100 : 90, p + 8)), 180);
    return () => window.clearInterval(t);
  }, [status, create.isSuccess]);
  useEffect(() => { if (status === 'copying' && progress >= 100) setStatus('done'); }, [status, progress]);

  const launch = () => {
    setStatus('copying');
    setProgress(0);
    create.mutate({ name: draft.name.trim(), perimeter, ratios, dju }, { onError: () => setStatus('error') });
  };

  const canNext = [
    !!draft.name.trim() && included.length > 0 && !draft.drawing,
    ratiosValid,
    djuValid,
  ][step] ?? true;
  const busy = status === 'copying';

  const impact = (
    <ImpactPanel buildings={included} ratios={ratios} dju={dju} defaultRatios={referential.defaultRatios} defaultDju={referential.defaultDju} />
  );

  return (
    <div role="presentation" style={{ position: 'absolute', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <UMFormStyles />
      <div role="dialog" aria-modal="true" aria-label="Nouveau projet" style={{ width: 1180, maxWidth: '100%', height: '100%', maxHeight: 820, background: UMR.white, display: 'flex', flexDirection: 'column', fontFamily: UMR.font, color: UMR.black, boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 24px', borderBottom: `1px solid ${UMR.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <PlanLogo size={32} />
            <h2 style={{ margin: 0, fontSize: 20, lineHeight: '28px', fontWeight: 700 }}>Nouveau projet réseau de chaleur</h2>
          </div>
          <button type="button" aria-label="Fermer" disabled={busy} onClick={onCancel} style={{ border: 'none', background: 'transparent', cursor: busy ? 'not-allowed' : 'pointer', padding: 0 }}>
            <UMIcon name="close" family="icons" size={24} color={UMR.textSecondary} />
          </button>
        </header>
        <Stepper current={step} />

        <div style={{ flex: 1, minHeight: 0 }}>
          {step === 0 && <StepPerimeter referential={referential} draft={draft} onChange={patch} included={included} includedIds={includedIds} />}
          {(step === 1 || step === 2) && (
            <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
              <div style={{ flex: 1, minWidth: 0, padding: 24, overflowY: 'auto' }}>
                {step === 1
                  ? <RatioTable value={ratios} defaults={referential.defaultRatios} onChange={setRatios} onValidityChange={setRatiosValid} />
                  : <DjuForm value={dju} defaults={referential.defaultDju} stations={referential.stations} onChange={setDju} onValidityChange={setDjuValid} />}
              </div>
              <aside style={{ width: 340, minWidth: 340, padding: 24, borderLeft: `1px solid ${UMR.border}`, background: UMR.grey100, overflowY: 'auto' }}>{impact}</aside>
            </div>
          )}
          {step === 3 && (
            <StepRecap
              name={draft.name.trim()}
              perimeterLabel={perimeterLabel}
              buildingCount={included.length}
              modifiedRatios={countModifiedRatios(ratios, referential.defaultRatios)}
              stationName={stationOf(dju).name}
              djuReference={dju.reference}
              coefficient={djuCoefficient(dju)}
              status={status}
              progress={progress}
            />
          )}
        </div>

        <footer style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 24px', borderTop: `1px solid ${UMR.border}` }}>
          <span style={{ fontSize: 12, color: UMR.textSecondary }}>{step < 3 ? `Étape ${step + 1} sur 3` : 'Vérifiez avant de lancer la copie'}</span>
          <span style={{ flex: 1 }} />
          {status === 'done' ? (
            <UMButton icon="arrow_forward" onClick={() => create.data && onOpen(create.data.id)}>Ouvrir le projet</UMButton>
          ) : (
            <>
              <UMButton variant="secondary" disabled={busy} onClick={onCancel}>Annuler</UMButton>
              {step > 0 && <UMButton variant="secondary" icon="arrow_back" disabled={busy} onClick={() => setStep((s) => s - 1)}>Précédent</UMButton>}
              {step < 3 && <UMButton disabled={!canNext} onClick={() => setStep((s) => s + 1)}>{step === 2 ? 'Vérifier' : 'Suivant'}</UMButton>}
              {step === 3 && <UMButton icon="content_copy" disabled={busy} onClick={launch}>{status === 'error' ? 'Réessayer' : 'Créer le projet'}</UMButton>}
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
